import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

import { CompassContextType } from '@/src/libs/CompassContext';
import { MeetingReport } from '@/types';

/**
 * Length caps enforced by the `reports` create rule in firestore.rules
 * (PAS-80). Keep the two in sync — these values exist so the write is
 * satisfiable by construction rather than by assumption about how long a
 * meeting name or id can get. The scrape-commit path bounds meeting-name
 * length not at all, so no fixed cap could otherwise be guaranteed safe, and a
 * rules rejection here is invisible to the user until PAS-82 lands.
 *
 * Rules `size()` counts UTF-16 code units, which is exactly what String.slice
 * counts — so slicing to the cap is what the rule measures.
 */
export const REPORT_FIELD_LIMITS = {
  issue: 64,
  meetingId: 128,
  meetingName: 512,
  note: 200,
} as const;

/**
 * Slice to `max` UTF-16 code units without leaving a lone high surrogate,
 * which would not be valid UTF-8 once encoded.
 */
const truncate = (value: string, max: number): string => {
  if (value.length <= max) return value;
  const sliced = value.slice(0, max);
  const last = sliced.charCodeAt(sliced.length - 1);
  const endsOnLoneHighSurrogate = last >= 0xd800 && last <= 0xdbff;
  return endsOnLoneHighSurrogate ? sliced.slice(0, -1) : sliced;
};

export const submitMeetingReport = async (
  meetingReport: MeetingReport,
  ctx: CompassContextType
) => {
  if (!ctx) throw new Error('No context provided.');
  if (!ctx.state.firestore) throw new Error('No firestore instance provided.');
  await addDoc(collection(ctx.state.firestore, 'reports'), {
    ...meetingReport,
    issue: truncate(meetingReport.issue as string, REPORT_FIELD_LIMITS.issue),
    meetingId: truncate(meetingReport.meetingId, REPORT_FIELD_LIMITS.meetingId),
    meetingName: truncate(
      meetingReport.meetingName,
      REPORT_FIELD_LIMITS.meetingName
    ),
    note: truncate(meetingReport.note, REPORT_FIELD_LIMITS.note),
    resolved: false,
    dateSubmitted: serverTimestamp(),
  });
};
